import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthMessageScreen from './AuthMessageScreen';
import { getCognitoLoginLang, getUserId, onSessionEstablished } from '../../auth/tokenStore';

// Shown in a tab when ANOTHER tab signs out (Cognito mode): the usual
// "You have been signed out / Sign in" page.
//
// It is a route, not an overlay on the live app: with <Admin> still mounted
// the tab would keep firing requests, get 401s and start a login on its own,
// racing the other tab's login (Amplify's OAuth transaction is shared between
// tabs, so concurrent logins block or invalidate each other).
//
// Clicking "Sign in" means the user wants THIS tab: the login goes through
// /init-login with takeOver, which clears whatever transaction another tab
// left before starting. A login form still open in the other tab is simply
// abandoned.
//
// Amplify's tokens are shared too: once a login completes anywhere, this tab
// enters the app by itself. That state is read from the user-info cache, not
// from fetchAuthSession(), which blocks while another tab's login is in
// flight.
//
// Rendered outside <Admin> (no i18nProvider): minimal fr/en strings.

const TEXTS = {
    fr: { title: 'Vous avez été déconnecté', body: 'Connectez-vous pour continuer.', action: 'Se connecter' },
    en: { title: 'You have been signed out', body: 'Sign in to continue.', action: 'Sign in' },
};

const SignedOut = () => {
    const navigate = useNavigate();
    const texts = TEXTS[getCognitoLoginLang()] || TEXTS.en;

    useEffect(() => {
        // A login already completed elsewhere (arrived late), or completes
        // while this page is shown: enter the app.
        if (getUserId()) {
            navigate('/dashboard', { replace: true });
            return undefined;
        }
        return onSessionEstablished(() => navigate('/dashboard', { replace: true }));
    }, [navigate]);

    const signIn = () => {
        if (getUserId()) {
            navigate('/dashboard', { replace: true });
            return;
        }
        navigate('/init-login', { replace: true, state: { takeOver: true } });
    };

    return (
        <AuthMessageScreen
            title={texts.title}
            body={texts.body}
            action={texts.action}
            onAction={signIn}
        />
    );
};

export default SignedOut;
